"use client";

import { ExternalLink, Instagram } from "lucide-react";
import Image from "next/image";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@louez/ui/components/carousel";

import type { PortSocialVideo } from "@/lib/port-home-content";
import { portSocialProfile } from "@/lib/port-social-fallback";

export const PortSocialVideos = ({ videos }: { videos: PortSocialVideo[] }) => {
  if (videos.length === 0) return null;

  return (
    <section id="social" className="scroll-mt-20 overflow-hidden bg-sky-950 py-14 text-white md:py-18">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-8 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
          <div>
            <span className="inline-flex items-center gap-3 text-xs font-bold tracking-[0.18em] text-sky-300 uppercase">
              <Image src={portSocialProfile.avatar} alt="Avatar de Náutica Álvarez" width={38} height={38} className="size-9 rounded-full border-2 border-white/80 object-cover" />
              <span><span className="block text-[10px] text-sky-300/75">Social media</span><span className="text-sm tracking-normal text-white normal-case">{portSocialProfile.username}</span></span>
            </span>
            <h2 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl">El puerto, en movimiento</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-sky-100/75">
              Historias, trabajos y momentos junto al mar desde Náutica Álvarez.
            </p>
          </div>
          <a
            href="https://www.instagram.com/nauticaalvarez/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold transition hover:bg-white/20"
          >
            @nauticaalvarez <ExternalLink className="size-3.5" />
          </a>
        </div>
      </div>

      <div className="w-full px-3 sm:px-5 lg:px-6">
        <Carousel opts={{ align: "start", loop: videos.length > 3 }}>
          <CarouselContent className="-ml-2 md:-ml-3">
            {videos.map((video) => (
              <CarouselItem key={video.id} className="basis-[84%] pl-2 sm:basis-[48%] md:pl-3 lg:basis-[32%] xl:basis-[24%] 2xl:basis-[20%]">
                <article className="group relative h-[28rem] overflow-hidden rounded-3xl border border-white/15 bg-slate-950 shadow-xl">
                  <div className="absolute inset-0 overflow-hidden bg-slate-950">
                    {video.kind === "video" ? (
                      <video className="h-full w-full object-cover" controls playsInline preload="metadata" poster={video.poster || undefined} src={video.src} />
                    ) : (
                      <Image src={video.src} alt={video.title} fill sizes="(max-width: 640px) 82vw, (max-width: 1024px) 48vw, 25vw" className="object-cover transition duration-500 group-hover:scale-[1.03]" />
                    )}
                    <span className="pointer-events-none absolute top-3 right-3 flex size-9 items-center justify-center rounded-full bg-black/45 backdrop-blur">
                      <Instagram className="size-4" />
                    </span>
                  </div>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent px-5 pt-20 pb-5">
                    <h3 className="text-lg font-bold text-white drop-shadow">{video.title}</h3>
                    {video.caption && <p className="mt-1 line-clamp-2 text-sm leading-5 text-sky-50/85 drop-shadow">{video.caption}</p>}
                    <a
                      href={video.instagramUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="pointer-events-auto mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-sky-300 transition group-hover:text-white"
                    >
                      Ver en Instagram <ExternalLink className="size-3" />
                    </a>
                  </div>
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-2 border-white/20 bg-sky-950/90 text-white hover:bg-white hover:text-sky-950" />
          <CarouselNext className="right-2 border-white/20 bg-sky-950/90 text-white hover:bg-white hover:text-sky-950" />
        </Carousel>
      </div>
    </section>
  );
};
